import { Box, Button, CircularProgress } from "@mui/material";
import { bindActionCreators } from "@reduxjs/toolkit";
import * as React from "react";
import { useQueryClient } from "react-query";
import { connect } from "react-redux";
import Texts from "../../../resources/Texts";
import { Service } from "../../../services/Service";
import { ApplicationState } from "../../../store";
import { actionCreators as AlertStoreActionCreators } from "../../../store/AlertStore";
import { handleErrorMessage } from "../../../utils/Utils";
import DialogBase from "../DialogBase";
import * as DialogStore from "../DialogStore";
import DeleteFileDialogContent from "./DeleteFileDialogContent";

type DeleteFileDialogProps = DialogStore.DialogState &
	typeof DialogStore.actionCreators &
	typeof AlertStoreActionCreators;

const DeleteFileDialog: React.FC<DeleteFileDialogProps> = ({
	deleteFileDialog,
	setDeleteFileDialogState,
	addAlert,
	addErrorAlert,
}) => {
	const queryClient = useQueryClient();
	const [deleteInProgress, setDeleteInProgress] = React.useState<boolean>(false);

	const handleClose = (): void => {
		setDeleteFileDialogState(null);
	};

	const handleSubmit = async (): Promise<void> => {
		setDeleteInProgress(true);
		try {
			await Service.deleteFiles(deleteFileDialog.fileBlobUniques);

			addAlert({
				type: "success",
				text:
					deleteFileDialog.fileBlobUniques.length > 1
						? `Success! ${deleteFileDialog.fileBlobUniques.length} files deleted`
						: `Success! ${deleteFileDialog.fileName} has been deleted from the system`,
			});

			queryClient.invalidateQueries("adminDocumentsData");

			handleClose();
		} catch (error) {
			addErrorAlert(handleErrorMessage(error));
		}
		setDeleteInProgress(false);
	};

	if (!deleteFileDialog) {
		return null;
	}

	return (
		<DialogBase
			open={deleteFileDialog.open}
			dialogTitle={
				<Box
					component="span"
					sx={{
						fontWeight: "600",
					}}
				>
					{Texts.Dialogs.DeleteFileDialog.Title}
				</Box>
			}
			topBorderColor="#FFC41C"
			onCancel={handleClose}
			customButtons={
				<>
					<Button
						onClick={handleClose}
						sx={{
							fontWeight: "600",
						}}
					>
						{Texts.Dialogs.Actions.Cancel}
					</Button>

					<Button
						onClick={handleSubmit}
						sx={{
							background: "#FFFFFF",
							borderRadius: "4px",
							color: "#151724",
							"&:hover": {
								backgroundColor: "#9AA0AF",
							},
						}}
					>
						{Texts.Dialogs.Actions.Ok}

						{deleteInProgress && (
							<CircularProgress color="inherit" size="15px" sx={{ ml: "4px" }} />
						)}
					</Button>
				</>
			}
		>
			<DeleteFileDialogContent fileName={deleteFileDialog.fileName} />
		</DialogBase>
	);
};

const mapStateToProps = (state: ApplicationState) => {
	return state.dialogs;
};

const mapDispatchToProps = (dispatch) => {
	return bindActionCreators(
		{
			...DialogStore.actionCreators,
			...AlertStoreActionCreators,
		},
		dispatch
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(DeleteFileDialog);
